import React, { useContext, useState, useEffect } from 'react';
import { Modal, Button, Slider, Input, Row, Col } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { View } from '@/styles/reactnative';
import KrwPrice from '@app/components/KrwPrice';
import BigNumber from 'bignumber.js';
import { getBigNumber } from '@utils/getBigNumber';
import {
  CalculatorContainer,
  TabSection,
  TabButton,
  InputSection,
  InputRow,
  InputLabel,
  StyledInput,
  ClearButton,
  SliderSection,
  CalculateButton,
  ResultCard,
  ResultRow,
  ResultLabel,
  ResultValue,
  SectionDivider,
} from './styles';

interface TradeCalculatorProps {
  open: boolean;
  onClose: () => void;
  EPs: any[];
  calcEP: number;
  setCalcEP: (id: number) => void;
}

const TradeCalculator: React.FC<TradeCalculatorProps> = ({
  open,
  onClose,
  EPs,
  calcEP,
  setCalcEP,
}) => {
  const { t } = useTranslation();
  const { theme, isDarkMode } = useContext(ThemeContext);

  // 상태
  const [tab, setTab] = useState<'PNL' | 'LIQUID'>('PNL');
  const [direction, setDirection] = useState<'LONG' | 'SHORT'>('LONG');
  const [leverage, setLeverage] = useState(10);

  // PNL 계산 입력
  const [entryPrice, setEntryPrice] = useState('');
  const [exitPrice, setExitPrice] = useState('');
  const [quantity, setQuantity] = useState('');

  // Liquidation 계산 입력
  const [margin, setMargin] = useState('');

  // 현재 선택된 거래쌍
  const currentEP = EPs?.find((item) => item.id === calcEP);
  const coin = currentEP?.pairs?.split('/')[0] || 'BTC';
  const maxLeverage = currentEP?.max_leverage || 125;

  // 레버리지 마커
  const leverageMarks: { [key: number]: string } = {};
  [1, 25, 50, 75, 100, maxLeverage].forEach((v) => {
    if (v <= maxLeverage) leverageMarks[v] = `${v}x`;
  });

  // PNL 계산
  const calculatePNL = () => {
    if (!entryPrice || !exitPrice || !quantity) return { margin: null, pnl: null };

    const entry = getBigNumber(entryPrice);
    const exit = getBigNumber(exitPrice);
    const qty = getBigNumber(quantity);
    const lev = getBigNumber(leverage);

    const margin = entry.multipliedBy(qty).dividedBy(lev);

    let pnl: BigNumber;
    if (direction === 'LONG') {
      pnl = exit.minus(entry).multipliedBy(qty);
    } else {
      pnl = entry.minus(exit).multipliedBy(qty);
    }

    return { margin, pnl };
  };

  // 청산가 계산
  const calculateLiquidation = () => {
    if (!entryPrice || !margin || !quantity) return null;

    const entry = getBigNumber(entryPrice);
    const mrg = getBigNumber(margin);
    const qty = getBigNumber(quantity);
    const lev = getBigNumber(leverage);

    const maintenanceRate = getBigNumber(0.004); // 0.4%

    let liquidPrice: BigNumber;
    if (direction === 'LONG') {
      liquidPrice = entry.minus(
        mrg.multipliedBy(getBigNumber(1).minus(maintenanceRate)).dividedBy(qty)
      );
    } else {
      liquidPrice = entry.plus(
        mrg.multipliedBy(getBigNumber(1).minus(maintenanceRate)).dividedBy(qty)
      );
    }

    return liquidPrice;
  };

  const pnlResult = tab === 'PNL' ? calculatePNL() : null;
  const liquidResult = tab === 'LIQUID' ? calculateLiquidation() : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      width={480}
      className={'p2pmodal' + (isDarkMode ? ' dark' : '')}
      style={{ top: 100 }}
    >
      <CalculatorContainer>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          {t('calculator')}
        </h2>

        {/* 거래쌍 선택 */}
        <InputSection>
          <select
            value={calcEP}
            onChange={(e) => setCalcEP(Number(e.target.value))}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: `1px solid ${isDarkMode ? '#333' : '#e0e0e0'}`,
              fontSize: 14,
              backgroundColor: isDarkMode ? '#1F1F1F' : '#fff',
              color: isDarkMode ? '#fff' : '#000',
            }}
          >
            {EPs?.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} ({item.pairs})
              </option>
            ))}
          </select>
        </InputSection>

        {/* 탭 */}
        <TabSection>
          <TabButton
            active={tab === 'PNL'}
            onClick={() => setTab('PNL')}
            $isDarkMode={isDarkMode}
          >
            {t('pnl')}
          </TabButton>
          <TabButton
            active={tab === 'LIQUID'}
            onClick={() => setTab('LIQUID')}
            $isDarkMode={isDarkMode}
          >
            {t('liquidation')}
          </TabButton>
        </TabSection>

        <SectionDivider $isDarkMode={isDarkMode} />

        {/* Long/Short 선택 */}
        <TabSection>
          <TabButton
            active={direction === 'LONG'}
            onClick={() => setDirection('LONG')}
            type="secondary"
            $isDarkMode={isDarkMode}
          >
            {t('long')}
          </TabButton>
          <TabButton
            active={direction === 'SHORT'}
            onClick={() => setDirection('SHORT')}
            type="secondary"
            $isDarkMode={isDarkMode}
          >
            {t('short')}
          </TabButton>
        </TabSection>

        {/* 레버리지 슬라이더 */}
        <SliderSection $isDarkMode={isDarkMode}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            {t('leverage')}: {leverage}x
          </div>
          <Slider
            min={1}
            max={maxLeverage}
            value={leverage}
            onChange={setLeverage}
            marks={leverageMarks}
            trackStyle={{ backgroundColor: '#007aff' }}
            railStyle={{
              backgroundColor: isDarkMode ? '#3a3a3a' : '#e0e0e0',
            }}
          />
        </SliderSection>

        {/* PNL 계산 입력 */}
        {tab === 'PNL' && (
          <InputSection>
            <InputRow $isDarkMode={isDarkMode}>
              <InputLabel $isDarkMode={isDarkMode}>{t('entryPrice')}</InputLabel>
              <StyledInput
                placeholder="0.00"
                value={entryPrice}
                onChange={(e) => setEntryPrice(e.target.value)}
                suffix="USDT"
                type="number"
                $isDarkMode={isDarkMode}
              />
              <ClearButton
                onClick={() => setEntryPrice('')}
                icon={<CloseOutlined />}
                size="small"
                $isDarkMode={isDarkMode}
              />
            </InputRow>

            <InputRow $isDarkMode={isDarkMode}>
              <InputLabel $isDarkMode={isDarkMode}>{t('exitPrice')}</InputLabel>
              <StyledInput
                placeholder="0.00"
                value={exitPrice}
                onChange={(e) => setExitPrice(e.target.value)}
                suffix="USDT"
                type="number"
                $isDarkMode={isDarkMode}
              />
              <ClearButton
                onClick={() => setExitPrice('')}
                icon={<CloseOutlined />}
                size="small"
                $isDarkMode={isDarkMode}
              />
            </InputRow>

            <InputRow $isDarkMode={isDarkMode}>
              <InputLabel $isDarkMode={isDarkMode}>{t('quantity')}</InputLabel>
              <StyledInput
                placeholder="0.00"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                suffix={coin}
                type="number"
                $isDarkMode={isDarkMode}
              />
              <ClearButton
                onClick={() => setQuantity('')}
                icon={<CloseOutlined />}
                size="small"
                $isDarkMode={isDarkMode}
              />
            </InputRow>

            {/* PNL 결과 */}
            {pnlResult && (pnlResult.margin || pnlResult.pnl) && (
              <ResultCard $isDarkMode={isDarkMode}>
                <ResultRow $isDarkMode={isDarkMode}>
                  <ResultLabel $isDarkMode={isDarkMode}>{t('margin')}</ResultLabel>
                  <ResultValue>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <KrwPrice
                        price={pnlResult.margin?.toNumber()}
                        color={false}
                        dp={2}
                        krw={true}
                        equiv={true}
                        margin={false}
                        fontSize="16px"
                        subFontSize="11px"
                      />
                    </div>
                  </ResultValue>
                </ResultRow>
                <ResultRow $isDarkMode={isDarkMode}>
                  <ResultLabel $isDarkMode={isDarkMode}>{t('pnlprice')}</ResultLabel>
                  <ResultValue>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <KrwPrice
                        price={pnlResult.pnl?.toNumber()}
                        ratemargin={pnlResult.margin?.toNumber()}
                        equiv={true}
                        krw={true}
                        margin={false}
                        fontSize="16px"
                        subFontSize="11px"
                      />
                    </div>
                  </ResultValue>
                </ResultRow>
              </ResultCard>
            )}
          </InputSection>
        )}

        {/* 청산가 계산 입력 */}
        {tab === 'LIQUID' && (
          <InputSection>
            <InputRow $isDarkMode={isDarkMode}>
              <InputLabel $isDarkMode={isDarkMode}>{t('entryPrice')}</InputLabel>
              <StyledInput
                placeholder="0.00"
                value={entryPrice}
                onChange={(e) => setEntryPrice(e.target.value)}
                suffix="USDT"
                type="number"
                $isDarkMode={isDarkMode}
              />
              <ClearButton
                onClick={() => setEntryPrice('')}
                icon={<CloseOutlined />}
                size="small"
                $isDarkMode={isDarkMode}
              />
            </InputRow>

            <InputRow $isDarkMode={isDarkMode}>
              <InputLabel $isDarkMode={isDarkMode}>{t('margin')}</InputLabel>
              <StyledInput
                placeholder="0.00"
                value={margin}
                onChange={(e) => setMargin(e.target.value)}
                suffix="USDT"
                type="number"
                $isDarkMode={isDarkMode}
              />
              <ClearButton
                onClick={() => setMargin('')}
                icon={<CloseOutlined />}
                size="small"
                $isDarkMode={isDarkMode}
              />
            </InputRow>

            <InputRow $isDarkMode={isDarkMode}>
              <InputLabel $isDarkMode={isDarkMode}>{t('qty')}</InputLabel>
              <StyledInput
                placeholder="0.00"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                suffix={coin}
                type="number"
                $isDarkMode={isDarkMode}
              />
              <ClearButton
                onClick={() => setQuantity('')}
                icon={<CloseOutlined />}
                size="small"
                $isDarkMode={isDarkMode}
              />
            </InputRow>

            {/* 청산가 결과 */}
            {liquidResult && (
              <ResultCard $isDarkMode={isDarkMode}>
                <ResultRow $isDarkMode={isDarkMode}>
                  <ResultLabel $isDarkMode={isDarkMode}>{t('estimatedPrice')}</ResultLabel>
                  <ResultValue>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <KrwPrice
                        price={liquidResult.toNumber()}
                        color={false}
                        dp={2}
                        krw={true}
                        equiv={true}
                        margin={false}
                        fontSize="16px"
                        subFontSize="11px"
                      />
                    </div>
                  </ResultValue>
                </ResultRow>
              </ResultCard>
            )}
          </InputSection>
        )}
      </CalculatorContainer>
    </Modal>
  );
};

export default TradeCalculator;
